/**
 * NetworkBanner — NEW-CRIT-6
 *
 * Renders a persistent, dismiss-resistant banner when the production
 * build is running against a non-mainnet network (typically testnet).
 *
 * Why
 * ---
 * `apps/frontend/.env.production` used to be committed with
 * `VITE_TON_NETWORK=testnet`, so a freshly cut release branch silently
 * shipped a testnet wallet UI to real users. We now:
 *   1. Keep the variable out of git (see `.gitignore`).
 *   2. Show a banner at the top of every page so the operator can
 *      catch a misconfigured deploy at a glance and the user knows
 *      not to send real money.
 *
 * Visibility rules
 * ----------------
 * - Mainnet:                  banner is never rendered.
 * - Testnet & PROD build:      red banner with explicit "no real money" copy.
 * - Testnet & dev build:       muted yellow banner (developer-friendly).
 *
 * The banner is intentionally ARIA-live so screen readers announce it
 * once on first paint.
 */

import { useTranslation } from "react-i18next";
import { env } from "@/shared/config/env";

export function NetworkBanner() {
  const { t } = useTranslation();
  const network = env.VITE_TON_NETWORK;
  const isProd = import.meta.env.PROD;

  if (network === "mainnet") return null;

  const isWarning = isProd; // testnet on prod build is the dangerous case
  const containerClass = isWarning
    ? "bg-red-600 text-white"
    : "bg-yellow-500/90 text-black";

  return (
    <div
      role={isWarning ? "alert" : "status"}
      aria-live={isWarning ? "assertive" : "polite"}
      data-testid="network-banner"
      className={`${containerClass} text-center text-xs font-medium py-1.5 px-3`}
    >
      <span className="inline-flex items-center gap-2">
        <span aria-hidden="true">{isWarning ? "⚠" : "ℹ"}</span>
        {t(isWarning ? "networkBanner.testnet" : "networkBanner.testnetDev")}
      </span>
    </div>
  );
}
